import { NON_DUPLICABLE_ITEM_TYPES } from 'constants/userCart'
import { isAccommodationItem, isBedbankItem, isBundledExperienceItem, isExperienceItem, isFlightItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import getObjectKey from 'lib/object/getObjectKey'
import type { MachineContext } from 'typings/app/machineContext'

// Helper to get cart display name
export const getCartDisplayName = (context: MachineContext.MultiCartMachineContext, cartId?: string): string => {
  const id = cartId ?? context.activeCartId
  if (!id || context.cartSummary.status !== 'loaded') return 'cart'

  const cart = context.cartSummary.data[id]
  return cart?.name === 'default' ? 'cart' : cart?.name || 'cart'
}

export const updateCartItemsInCart = (
  context: MachineContext.MultiCartMachineContext,
  cartId: string,
  updater: (items: Record<string, App.MultiCartItem>) => Record<string, App.MultiCartItem>,
) => {
  if (context.cartItems.status !== 'loaded') {
    return context.cartItems
  }

  const updatedCartItems = { ...context.cartItems.data }

  if (updatedCartItems[cartId]) {
    updatedCartItems[cartId] = updater({ ...updatedCartItems[cartId] })
  }

  return {
    status: 'loaded' as const,
    data: updatedCartItems,
  }
}

function filterNonDuplicableItems(itemsToAdd: Array<App.Checkout.AnyItem>, existingItems: MachineContext.CartItems['any']) {
  // For item types that can only have one item in the cart, filter out new items if that type already exists
  const existingItemTypes = new Set(Object.values(existingItems).map(item => item.item.itemType))
  const itemTypesToFilter = new Set<App.Checkout.AnyItem['itemType']>(
    NON_DUPLICABLE_ITEM_TYPES.filter(type => existingItemTypes.has(type)),
  )
  return itemsToAdd.filter(item => !itemTypesToFilter.has(item.itemType))
}

function getStableOccupancy(occupancy: App.Occupants | App.RoomOccupants) {
  return {
    adults: occupancy.adults,
    children: occupancy.children,
    childrenAge: occupancy.childrenAge,
    infants: occupancy.infants,
  }
}

function getAbandonItemBedbankKey(item: App.Checkout.BedbankHotelItem) {
  const { offerId, boardCode, roomId, refundable, occupancy, checkIn, checkOut } = item
  return getObjectKey({
    offerId,
    boardCode,
    roomId,
    refundable,
    stableOccupancy: getStableOccupancy(occupancy),
    checkIn,
    checkOut,
  })
}

function getAbandonItemHotelKey(item: App.Checkout.InstantBookingLEHotelItem) {
  const { offerId, packageId, occupancy, checkIn, checkOut } = item
  return getObjectKey({
    offerId,
    packageId,
    stableOccupancy: getStableOccupancy(occupancy),
    checkIn,
    checkOut,
  })
}

function getAbandonItemFlightKey(item: App.Checkout.FlightItem) {
  const { occupants, travelStart, travelEnd, flights } = item
  return getObjectKey({
    occupants,
    travelStart,
    travelEnd,
    flights: flights.map(flight => ({ journeyKey: flight.journeyKey, fareFamily: flight.fareFamily?.fareFamily })),
  })
}

function getAbandonItemKey(item: App.Checkout.AnyItem): string | undefined {
  if (isBedbankItem(item)) return getAbandonItemBedbankKey(item)
  if (isInstantBookingLEHotelItem(item)) return getAbandonItemHotelKey(item)
  if (isFlightItem(item)) return getAbandonItemFlightKey(item)
}

function filterAlreadyAbandonedItems(itemsToAdd: Array<App.Checkout.AnyItem>, existingItems: MachineContext.CartItems['any']) {
  const existingItemKeys = new Set(
    Object.values(existingItems)
      .filter(item => item.state !== 'deleted')
      .map(item => getAbandonItemKey(item.item))
      .filter((key): key is string => !!key),
  )

  if (!existingItemKeys.size) return itemsToAdd

  return itemsToAdd.filter(item => {
    const key = getAbandonItemKey(item)
    return !key || !existingItemKeys.has(key)
  })
}

// New utility function for getting deduped items to add to cart
export function getDedupedItemsForCart(
  context: MachineContext.MultiCartMachineContext,
  items: Array<App.Checkout.AnyItem>,
  dedupeAbandonedItems: boolean = false,
  cartId: string | undefined = context.activeCartId,
): Array<App.Checkout.AnyItem> {
  const existingCartItemsByCart = context.cartItems.status === 'loaded' ? context.cartItems.data : {}
  const existingCartItems = cartId ? existingCartItemsByCart[cartId] ?? {} : {}
  const duplicableItems = filterNonDuplicableItems(items, existingCartItems)
  return dedupeAbandonedItems
    ? filterAlreadyAbandonedItems(duplicableItems, existingCartItems)
    : duplicableItems
}

// Utility to check if items can be added to active cart
export function getHasItemsToAdd(
  context: MachineContext.MultiCartMachineContext,
  items: Array<App.Checkout.AnyItem>,
  dedupeAbandonedItems: boolean = false,
  cartId: string | undefined = context.activeCartId,
): boolean {
  // Check if there's an active cart
  if (!cartId) {
    return false
  }

  // Check if cart items are loaded
  if (context.cartItems.status !== 'loaded') {
    return false
  }

  // Check if there are remaining items to add after deduplication
  const dedupedItems = getDedupedItemsForCart(context, items, dedupeAbandonedItems, cartId)
  return !!dedupedItems.length
}

export function expandWithBundledChildren(
  items: Record<string, App.MultiCartItem>,
  itemIds: Array<string>,
): Array<string> {
  const expandedIds = new Set(itemIds)
  const parentInnerItemIds = new Set(
    itemIds
      .filter((itemId) => items[itemId]?.item?.itemId && isAccommodationItem(items[itemId].item))
      .map((itemId) => items[itemId].item.itemId),
  )

  if (parentInnerItemIds.size === 0) return Array.from(expandedIds)

  for (const candidate of Object.values(items)) {
    if (candidate.state === 'deleted') continue

    if (isBundledExperienceItem(candidate.item)) {
      const experienceOrTransfer = candidate.item
      const isParentAccomBundle = !!experienceOrTransfer.isAccomBundle && !!experienceOrTransfer.bundledItemIds?.some(parentInnerItemId => parentInnerItemIds.has(parentInnerItemId))
      const isParentPassport = isExperienceItem(experienceOrTransfer) && !!experienceOrTransfer.accommodationParentItemId && parentInnerItemIds.has(experienceOrTransfer.accommodationParentItemId)

      if (isParentAccomBundle || isParentPassport) {
        expandedIds.add(candidate.id)
      }
    }
  }
  return Array.from(expandedIds)
}

/**
 * Stable actor id for multi-cart soft-delete/undo.
 * Delete and undo UIs can pass the same item set in different orders
 * (e.g. hotel-first from LeHotelTile vs date-sorted FlightHotelBundle undo),
 * so the id must not depend on array order.
 */
export function getMultiCartDeleteItemMachineId(itemIds: Array<string>): string {
  return `deleteItemMachine-${[...itemIds].sort().join('-')}`
}
